"use client";

import { Bars3Icon, MagnifyingGlassIcon, UserCircleIcon } from "@heroicons/react/20/solid";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import Logo from "~/src/components/common/Logo";
import { WINDOW_MSG_LOGOUT } from "~/src/const/ui/window/window";
import { fbSignOut } from "~/src/lib/authentication/auth";
import { createNewAnalysisDocumentId } from "~/src/lib/firestore/analysis";
import {
  getMypageUrl,
  getSettingsUrl,
  QUERY_PARAM_VALUE_NEW,
  transCreateAnalysisPage,
} from "~/src/lib/ui/transition";
import {
  useAuthContext,
  USER_AUTH_INIT,
  UserAuthState,
} from "~/src/stores/context/auth/AuthContext";
import { useMypageCommonSetContext } from "~/src/stores/context/mypage/MypageCommonContext";
import Dropdown from "../../common/Dropdown";
import NavButton from "../../common/NavBotton";
import SearchForm from "../../common/SearchForm";

type NavbarProps = {
  isMypage?: boolean;
  isModeSwitch?: boolean;
};

//------------
// モードデザイン
//------------
const LIGHT_NAV = "bg-WHITE_1";
const DARK_NAV = "bg-WHITE_1/0";

/**
 * Navbar
 */
export default function Navbar({ isMypage, isModeSwitch }: NavbarProps) {
  const { user } = useAuthContext();
  const router = useRouter();
  const [expandSearchArea, setExpandSearchArea] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(isModeSwitch !== undefined);
  const { setIsSidebarOpen } = useMypageCommonSetContext();

  /**
   * 横幅リサイズ関連
   */
  useEffect(() => {
    //------------
    // 検索フォーム強制クローズ設定
    //------------
    const mediaQuery = window.matchMedia("(min-width: 1024px)");

    // md以上になったら 強制で検索フォームを閉じる
    const handleResize = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        setExpandSearchArea(false);
      }
    };

    // 初回チェック
    handleResize(mediaQuery);

    // リスナー登録
    mediaQuery.addEventListener("change", handleResize);

    // クリーンアップでリスナーを削除
    return () => mediaQuery.removeEventListener("change", handleResize);
  }, []);

  /**
   * スクロールイベント関連
   */
  useEffect(() => {
    if (!isModeSwitch || isModeSwitch === undefined) return;

    //------------
    // スクロール時のデザイン切り替え
    //------------
    const handleScroll = () => {
      // 一定量スクロール下移動量
      const isScrolled = window.scrollY > 320;

      //------------
      // スクロール結果を反映
      //------------
      // ライトモードに切り替え
      if (isScrolled && isDarkMode) {
        setIsDarkMode(false);
        return;
      }

      // ダークモードに切り替え
      if (!isScrolled && !isDarkMode) {
        setIsDarkMode(true);
        return;
      }
    };

    //------------
    // スクロールイベント登録
    //------------
    window.addEventListener("scroll", handleScroll);

    //------------
    // クリーンアップ
    //------------
    // リスナー削除
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isDarkMode]); // eslint-disable-line

  /**
   * 検索フォーム表示トグル
   */
  const toggleExpandSearchArea = () => setExpandSearchArea(!expandSearchArea);

  return (
    <header
      className={`sticky top-0 z-50 w-full px-4 py-2 shadow-sm transition-colors duration-200 ease-in-out ${
        (isDarkMode ?? LIGHT_NAV) ? DARK_NAV : LIGHT_NAV
      }`}
    >
      <div className="flex items-center justify-between">
        {/* -- 左端 -- */}
        <div className="flex items-center">
          {/* マイページでlg未満の場合 */}
          {isMypage && (
            <div className="mr-5 inline-block lg:hidden">
              <Bars3Icon
                onClick={() => setIsSidebarOpen(true)}
                aria-hidden="true"
                className="size-6"
              />
            </div>
          )}
          {/* サイトロゴ */}
          <Logo isDarkMode={isDarkMode} />
        </div>

        {/* -- 中央 -- */}
        <div className="flex items-center">
          {/* lg以上：検索フォーム */}
          <div className="hidden w-128 lg:inline-block lg:w-152">
            <SearchForm isDarkMode={isDarkMode} />
          </div>
        </div>

        {/* -- 右端 -- */}
        <div className="flex items-center">
          <div className="inline-flex list-none items-center gap-2 lg:pr-5">
            {/* lg未満：検索アイコン */}
            <MagnifyingGlassIcon
              aria-hidden="true"
              className="mr-2 size-5 text-GRAY_2 lg:hidden"
              onClick={toggleExpandSearchArea}
            />

            {/* Creators ドロップダウン */}
            <div>
              <Dropdown
                icon={<NavButton isDarkMode={isDarkMode}>Creators</NavButton>}
                items={[
                  { label: "創作の源泉を辿る", onClick: () => router.push("/influence") },
                  { label: "創作者の言葉", onClick: () => router.push("/recommend") },
                ]}
                menuWidth="w-40"
                isDarkMode={isDarkMode}
              />
            </div>

            {/* アイコン */}
            <Icons
              user={user}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      </div>

      {/*
       * スマホ・タブレット用検索フォーム
       * md未満のみ表示
       */}
      {expandSearchArea && (
        <div className="mt-3">
          <SearchForm isDarkMode={isDarkMode} />
        </div>
      )}
    </header>
  );
}

/**
 * ナビバー右アイコン群
 */
type IconsProps = {
  user: UserAuthState;
  isDarkMode: boolean;
};

function Icons({ user, isDarkMode }: IconsProps) {
  const router = useRouter();

  /**
   * マイページメニュークリックハンドラ
   */
  const onClickMypage = () => {
    if (!user) return;

    const url = getMypageUrl();
    router.push(url);
  };

  /**
   * 設定メニュークリックハンドラ
   */
  const onClickSettings = () => {
    if (!user) return;

    const url = getSettingsUrl();
    router.push(url);
  };

  /**
   * サインアウトクリック処理
   */
  const onClickSignOut = () => {
    //------------
    // サインアウト
    //------------
    fbSignOut();

    //------------
    // メッセージの表示
    //------------
    window.alert(WINDOW_MSG_LOGOUT);
  };

  /**
   * 分析作成クリック処理
   */
  const onClickArticle = () => {
    if (!user) return;

    //------------
    // 分析作成画面へ遷移
    //------------
    // 分析記事ドキュメントIDを発行
    const docAysisId = createNewAnalysisDocumentId();
    // 遷移
    transCreateAnalysisPage(router, docAysisId, QUERY_PARAM_VALUE_NEW);
  };

  //------------
  // アイコンリスト
  //------------
  const menuUserItems = [
    {
      label: "マイページ",
      onClick: onClickMypage,
    },
    {
      label: "設定",
      onClick: onClickSettings,
    },
    // {
    //   label: "下書き一覧",
    //   onClick: onClickDraft,
    // },
    {
      label: "ログアウト",
      onClick: onClickSignOut,
    },
  ];

  const menuArticleItems = [
    {
      label: "新規作成",
      onClick: onClickArticle,
    },
    // {
    //   label: "下書き",
    //   onClick: onClickDraft,
    // },
  ];

  //================================================
  // レンダリング
  //================================================
  //------------
  // 認証状態が未確定
  //------------
  if (user === USER_AUTH_INIT) {
    return;
  }

  //------------
  // 認証状態が確定
  //------------
  return user ? (
    // /* ログイン中 */
    <>
      <Dropdown
        icon={
          // 不具合検証メモ「分析投稿」を押下してメニューを表示したあと、このアイコンをクリックしても、メニューが表示されたままとなる問題。
          // ここをIconではなく、文字に変更すると、ちゃんと閉じてくれる。
          // そこまで分かったがiconのままでの対策方法は思いつかない。
          <UserCircleIcon
            aria-hidden="true"
            className="inline-flex size-8 items-center text-PRIMARY hover:text-PRIMARY/80"
          />
        }
        items={menuUserItems}
        isDarkMode={isDarkMode}
      />
      {/* 分析記事作成メニュー */}
      <Dropdown
        icon={<NavButton isDarkMode={isDarkMode}>分析投稿</NavButton>}
        items={menuArticleItems}
        isDarkMode={isDarkMode}
      />
    </>
  ) : (
    // 未ログイン
    <>
      <NavButton isDarkMode={isDarkMode}>
        <Link href="/sign-in">ログイン</Link>
      </NavButton>
      <NavButton isDarkMode={isDarkMode}>
        <Link href="/sign-up">新規登録</Link>
      </NavButton>
    </>
  );
}
